import React from "react";
import { Link } from "react-router-dom";
import {
  HandHeart,
  LockKey,
  ShieldCheck,
  HeartStraight,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { IMAGES } from "../lib/site";
import Seo from "../components/Seo";

// Single source of truth for the PayPal hosted donation button.
// All giving amount, frequency, and donor-detail entry happens on PayPal.
const PAYPAL_DONATE_URL =
  "https://www.paypal.com/donate/?hosted_button_id=YUT5HH3HDHFJL";

const Donate = () => {
  return (
    <div data-testid="page-donate">
      <Seo
        title="Donate & Partner With Our Ministry"
        description="Support faith-based grief care and spiritual encouragement. Your gift to Begin Again Living Ministries helps families walking through loss and life's storms."
      />
      <section className="py-20 sm:py-28">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          {/* Left — Ministry explanation */}
          <div className="lg:col-span-6 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Partner with the ministry
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
              Your gift carries hope to someone in their hardest hour.
            </h1>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed">
              Every donation directly supports grief outreach, free workshops,
              printed resources, and chaplain-led encouragement for people who
              can&rsquo;t afford care during their most fragile season.
            </p>

            <div className="mt-10 rounded-2xl overflow-hidden border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <img
                src={IMAGES.prayingHands}
                alt="Hands held in prayer"
                className="w-full h-64 object-cover"
              />
            </div>

            <div className="mt-8 space-y-4">
              {[
                { icon: HeartStraight, t: "Grief outreach to bereaved families" },
                { icon: HandHeart, t: "Free workshops & devotional resources" },
                { icon: ShieldCheck, t: "Confidential chaplain-led encouragement" },
              ].map(({ icon: Icon, t }) => (
                <div key={t} className="flex items-start gap-3 text-ink-700">
                  <Icon
                    size={22}
                    weight="duotone"
                    className="text-primary mt-0.5 shrink-0"
                  />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Single CTA card */}
          <div className="lg:col-span-6 reveal">
            <div
              data-testid="donate-cta-card"
              className="bg-white rounded-2xl border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 sm:p-10"
            >
              <h2 className="font-heading text-2xl text-ink-900 font-medium">
                Give with confidence
              </h2>
              <p className="mt-3 text-ink-700 leading-relaxed">
                Begin Again Living Ministries partners with PayPal for every
                gift. You choose your amount, giving frequency, and any
                designation directly on PayPal&rsquo;s secure page &mdash; no
                duplicate forms, no shared bank details.
              </p>

              <a
                href={PAYPAL_DONATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="donate-paypal-button"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-primary text-white rounded-full px-7 py-4 font-semibold text-base hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                Donate Securely with PayPal
                <ArrowUpRight size={18} weight="bold" />
              </a>

              <p
                data-testid="donate-paypal-note"
                className="mt-4 text-sm text-ink-700 leading-relaxed text-center"
              >
                You&rsquo;ll be securely redirected to PayPal to complete your
                gift and select any available giving options.
              </p>

              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-ink-500">
                <LockKey size={14} weight="duotone" />
                Secure SSL checkout &mdash; PayPal never shares your card or
                bank details with us.
              </div>
            </div>

            <p className="mt-6 text-xs text-ink-500 leading-relaxed text-center">
              Prefer to give a different way? <Link to="/contact" className="text-primary font-semibold underline decoration-primary/30 hover:decoration-primary">Contact the ministry</Link> and we&rsquo;ll be honored to help.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-softblue/40">
        <div className="container-x grid md:grid-cols-3 gap-8 text-center">
          {[
            { value: "100%", label: "Goes directly to grief outreach and ministry programs" },
            { value: "Trusted", label: "Stewarded with care, transparency, and prayer" },
            { value: "Secure", label: "Donations processed through PayPal — your bank info is never shared with us" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-8 border border-ink-100">
              <div className="font-heading text-4xl font-medium text-primary">{s.value}</div>
              <div className="mt-3 text-ink-700">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="container-x mt-12">
          <p
            data-testid="donate-501c3"
            className="max-w-3xl mx-auto text-center text-sm text-ink-500 leading-relaxed"
          >
            Begin Again Living Ministries is a registered 501(c)(3) nonprofit
            organization dedicated to providing faith-based encouragement,
            grief support, spiritual care resources, and community support.
            Contributions may be tax-deductible to the extent allowed by law
            &mdash; please consult your tax advisor for guidance.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Donate;
